import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, ShieldCheck, Mail, Star, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";

const authHeaders = () => {
  const t = localStorage.getItem(AUTH_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const FORM_LABELS = {
  veteran: "Veteran",
  training_partner: "Training",
  employer_partner: "Employer",
  esg_partner: "ESG & Brand",
  supporter: "Supporter",
};

/** AdminImpactPage — /admin/impact
 *  Veteran Green Initiative operations: intake inbox + First Veteran
 *  Spotlight controls, kept separate from the financial dashboard. */
export default function AdminImpactPage() {
  const [authed, setAuthed] = useState(null);
  const [data, setData] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [invites, setInvites] = useState(null);
  const [inviteState, setInviteState] = useState("idle"); // idle | busy | sent
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetch(`${API}/api/admin/me`, { credentials: "include", headers: authHeaders() })
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  const load = useCallback(() => {
    fetch(`${API}/api/admin/impact/interest?_t=${Date.now()}`, {
      credentials: "include", headers: authHeaders(), cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
    fetch(`${API}/api/impact/spotlight?_t=${Date.now()}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setSpotlight)
      .catch(() => {});
    fetch(`${API}/api/admin/reviews?_t=${Date.now()}`, {
      credentials: "include", headers: authHeaders(), cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setReviews)
      .catch(() => {});
    fetch(`${API}/api/admin/reviews/invites/eligible?_t=${Date.now()}`, {
      credentials: "include", headers: authHeaders(), cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setInvites)
      .catch(() => {});
  }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  const toggleMilestone = async (key, done) => {
    await fetch(`${API}/api/admin/impact/spotlight`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ key, done }),
    }).catch(() => {});
    load();
  };

  const moderateReview = async (id, approved) => {
    await fetch(`${API}/api/admin/reviews/${id}/moderate`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ approved }),
    }).catch(() => {});
    load();
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    await fetch(`${API}/api/admin/reviews/${id}`, {
      method: "DELETE", credentials: "include", headers: authHeaders(),
    }).catch(() => {});
    load();
  };

  const sendInvites = async () => {
    if (!window.confirm(`Email a one-tap review invite to ${invites?.eligible ?? 0} long-time subscriber(s)? Each address is only ever invited once.`)) return;
    setInviteState("busy");
    await fetch(`${API}/api/admin/reviews/invites/send`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json", ...authHeaders() },
    }).catch(() => {});
    setInviteState("sent");
    load();
  };

  if (authed === false) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-sm text-zinc-300 mb-3">Admin sign-in required.</div>
          <Link to="/admin" className="text-[11px] font-mono uppercase tracking-wider text-[#39FF14]">← Go to admin login</Link>
        </div>
      </div>
    );
  }

  const rows = (data?.rows || []).filter((r) => filter === "all" || r.form_type === filter);
  const manual = (spotlight?.milestones || []).filter((m) => ["structure", "sponsored"].includes(m.key));

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-impact-page">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-black/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin" data-testid="impact-back-admin" className="text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">EVIQ · Admin</div>
              <div className="text-sm font-semibold truncate">Veteran Impact</div>
            </div>
          </div>
          <button
            onClick={load}
            data-testid="impact-admin-refresh"
            className="px-3 py-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors text-zinc-400 text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Spotlight controls */}
        <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="impact-spotlight-admin">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
            <ShieldCheck className="w-3.5 h-3.5 text-[#00E5FF]" /> First Veteran Spotlight
          </div>
          <p className="font-mono text-[10px] text-zinc-600 mb-4">
            Public launch tracker on eviq.pro/impact · 3 milestones auto-complete from submissions, 2 are yours to flip
          </p>
          {spotlight ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.07)" }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.max(2, spotlight.progress_pct)}%`, background: "linear-gradient(90deg,#39FF14,#00E5FF)" }} />
                </div>
                <span className="font-mono text-[11px] text-[#39FF14] tabular-nums shrink-0" data-testid="impact-admin-pct">
                  {spotlight.done}/{spotlight.total} · {spotlight.progress_pct}%
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {spotlight.milestones.map((m) => {
                  const isManual = ["structure", "sponsored"].includes(m.key);
                  return (
                    <div key={m.key} className="rounded-xl border border-white/10 bg-black/40 px-3.5 py-3" style={{ opacity: m.done ? 1 : 0.65 }}>
                      <div className="text-[12px] text-white leading-tight">{m.done ? "✓ " : ""}{m.title}</div>
                      <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">{m.detail}</div>
                      {isManual ? (
                        <button
                          onClick={() => toggleMilestone(m.key, !m.done)}
                          data-testid={`impact-spotlight-toggle-${m.key}`}
                          className="mt-2 rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider transition-colors"
                          style={m.done
                            ? { borderColor: "rgba(57,255,20,0.5)", color: "#39FF14", background: "rgba(57,255,20,0.08)" }
                            : { borderColor: "rgba(255,255,255,0.18)", color: "#a1a1aa" }}
                        >
                          {m.done ? "Mark not done" : "Mark done"}
                        </button>
                      ) : (
                        <div className="mt-2 font-mono text-[9px] uppercase tracking-wider text-zinc-600">
                          {m.done ? "Auto · complete" : "Auto · waiting on submissions"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-xs text-zinc-500 font-mono">Loading…</div>
          )}
        </section>

        {/* Owner reviews moderation */}
        <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="reviews-admin-card">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
            <Star className="w-3.5 h-3.5 text-[#39FF14]" /> Owner reviews
          </div>
          <p className="font-mono text-[10px] text-zinc-600 mb-4">
            Submissions from eviq.pro/reviews · {reviews?.pending ?? "…"} pending · {reviews?.total ?? "…"} total ·
            approved reviews go live instantly; search rating returns at 5+ approved
          </p>
          <div
            className="mb-4 rounded-xl border border-[#00E5FF]/25 bg-[#00E5FF]/[0.04] px-3.5 py-3 flex flex-wrap items-center justify-between gap-3"
            data-testid="review-invites-block"
          >
            <div>
              <div className="text-[12px] text-white font-medium">Review invites — long-time subscribers</div>
              <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                {invites === null
                  ? "Loading eligibility…"
                  : `${invites.eligible} eligible (30+ day paid subscribers, never reviewed/invited) · ${invites.already_invited} already invited`}
              </div>
            </div>
            {inviteState === "sent" ? (
              <span className="font-mono text-[10px] uppercase tracking-wider text-[#39FF14]" data-testid="review-invites-sent">
                ✓ Invites queued — sending in background
              </span>
            ) : (
              <button
                onClick={sendInvites}
                disabled={inviteState === "busy" || !invites?.eligible}
                data-testid="review-invites-send"
                className="rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors disabled:opacity-40"
                style={{ borderColor: "rgba(0,229,255,0.5)", color: "#00E5FF", background: "rgba(0,229,255,0.08)" }}
              >
                {inviteState === "busy" ? "Sending…" : `Email one-tap invites (${invites?.eligible ?? 0})`}
              </button>
            )}
          </div>
          {reviews === null ? (
            <div className="text-xs text-zinc-500 font-mono">Loading…</div>
          ) : reviews.rows.length === 0 ? (
            <div className="text-xs text-zinc-500" data-testid="reviews-admin-empty">
              No reviews yet — they'll appear here the moment an owner submits one on /reviews.
            </div>
          ) : (
            <div className="space-y-2">
              {reviews.rows.map((r) => (
                <div
                  key={r.id}
                  className="rounded-xl border px-3.5 py-3"
                  style={{
                    borderColor: r.approved ? "rgba(57,255,20,0.3)" : "rgba(255,176,32,0.35)",
                    background: r.approved ? "rgba(57,255,20,0.03)" : "rgba(255,176,32,0.04)",
                  }}
                  data-testid="reviews-admin-row"
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-[12px] text-white font-medium">{r.name}</span>
                    <span className="font-mono text-[10px] text-[#39FF14]">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                    {r.verified_owner && (
                      <span className="font-mono text-[9px] uppercase tracking-wider text-[#00E5FF]">verified owner</span>
                    )}
                    <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: r.approved ? "#39FF14" : "#FFB020" }}>
                      {r.approved ? "live" : "pending"}
                    </span>
                    <span className="font-mono text-[10px] text-zinc-500">
                      {r.email} · {r.vehicle || "—"} · {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-400 leading-relaxed mt-1.5">{r.body}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => moderateReview(r.id, !r.approved)}
                      data-testid={`review-moderate-${r.id}`}
                      className="rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider transition-colors"
                      style={r.approved
                        ? { borderColor: "rgba(255,176,32,0.5)", color: "#FFB020" }
                        : { borderColor: "rgba(57,255,20,0.5)", color: "#39FF14", background: "rgba(57,255,20,0.08)" }}
                    >
                      {r.approved ? "Unpublish" : "Approve & publish"}
                    </button>
                    <button
                      onClick={() => deleteReview(r.id)}
                      data-testid={`review-delete-${r.id}`}
                      className="rounded-full border border-white/15 px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-zinc-400 hover:text-[#FF6B57] hover:border-[#FF6B57]/40 transition-colors inline-flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Intake inbox */}
        <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="impact-inbox-card">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
            <Mail className="w-3.5 h-3.5 text-[#00E5FF]" /> Impact inbox
          </div>
          <p className="font-mono text-[10px] text-zinc-600 mb-4">
            Interest submissions from eviq.pro/impact and /partners · {data?.total ?? "…"} total · each one also emailed to you instantly
          </p>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="impact-inbox-counts">
            <button
              onClick={() => setFilter("all")}
              className="rounded-full border px-2.5 py-1 font-mono text-[10px] transition-colors"
              style={filter === "all"
                ? { borderColor: "rgba(57,255,20,0.5)", color: "#39FF14", background: "rgba(57,255,20,0.08)" }
                : { borderColor: "rgba(255,255,255,0.15)", color: "#a1a1aa" }}
              data-testid="impact-filter-all"
            >
              All · {data?.total ?? 0}
            </button>
            {Object.entries(FORM_LABELS).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className="rounded-full border px-2.5 py-1 font-mono text-[10px] transition-colors"
                style={filter === k
                  ? { borderColor: "rgba(0,229,255,0.6)", color: "#00E5FF", background: "rgba(0,229,255,0.1)" }
                  : { borderColor: "rgba(0,229,255,0.25)", color: "#00E5FF", background: "rgba(0,229,255,0.04)" }}
                data-testid={`impact-filter-${k}`}
              >
                {label} · {data?.counts?.[k] || 0}
              </button>
            ))}
          </div>
          {data === null ? (
            <div className="text-xs text-zinc-500 font-mono">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-zinc-500" data-testid="impact-inbox-empty">
              No submissions{filter !== "all" ? " for this type" : ""} yet — they'll appear the moment a veteran or partner raises their hand.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="impact-inbox-table">
                <thead>
                  <tr className="font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500 border-b border-white/10">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Org</th>
                    <th className="py-2 pr-3">Location</th>
                    <th className="py-2 pr-3">Interest</th>
                    <th className="py-2 pr-3">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 text-[11px] text-zinc-300 align-top" data-testid="impact-inbox-row">
                      <td className="py-2 pr-3 font-mono text-[10px] text-zinc-500 whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </td>
                      <td className="py-2 pr-3">
                        <span className="rounded-full border border-[#39FF14]/25 bg-[#39FF14]/[0.06] px-2 py-0.5 font-mono text-[9px] uppercase text-[#39FF14] whitespace-nowrap">
                          {FORM_LABELS[r.form_type] || r.form_type}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-white whitespace-nowrap">{r.name}</td>
                      <td className="py-2 pr-3 font-mono text-[10px] text-zinc-400">
                        <a href={`mailto:${r.email}`} className="hover:text-white transition-colors">{r.email}</a>
                      </td>
                      <td className="py-2 pr-3 text-zinc-400">{r.organization || "—"}</td>
                      <td className="py-2 pr-3 text-zinc-400 whitespace-nowrap">{r.location || "—"}</td>
                      <td className="py-2 pr-3 text-zinc-400">{r.area_of_interest || "—"}</td>
                      <td className="py-2 pr-3 text-zinc-500 max-w-[240px]">{r.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
